import { useRef, useState, useEffect } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { pastEngagements } from '@/mocks/schedule';

interface Engagement {
  id: string;
  date: string;
  organization: string;
  role: string;
  production: string;
  location: string;
  url: string;
  status: string;
}

function PastCard({ engagement, index }: { engagement: Engagement; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="group py-6 md:py-8 first:pt-0 transition-all duration-800 ease-out"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.8s cubic-bezier(0.25, 0.1, 0.25, 1) ${index * 0.07}s, transform 0.8s cubic-bezier(0.25, 0.1, 0.25, 1) ${index * 0.07}s`,
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 items-baseline">
        {/* Date */}
        <div className="md:col-span-3">
          <p className="text-sm tracking-[0.08em] text-foreground-500">
            {engagement.date}
          </p>
        </div>

        {/* Details */}
        <div className="md:col-span-6">
          <p className="text-foreground-950 text-base md:text-lg font-heading leading-snug">
            {engagement.production || engagement.role}
          </p>
          <p className="text-foreground-600 text-sm mt-1">
            <span className="text-accent-600 font-medium">{engagement.organization}</span>
            {engagement.role && engagement.production && (
              <span className="text-foreground-500"> — {engagement.role}</span>
            )}
          </p>
        </div>

        {/* Location + Link */}
        <div className="md:col-span-3 flex flex-col md:items-end gap-2">
          <p className="text-foreground-400 text-sm">{engagement.location}</p>
          {engagement.url && (
            <a
              href={engagement.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-foreground-500 hover:text-accent-600 text-xs tracking-[0.1em] uppercase transition-colors duration-300 group/link cursor-pointer"
            >
              <span className="whitespace-nowrap">Details</span>
              <span className="w-5 h-5 rounded-full border border-foreground-300 flex items-center justify-center group-hover/link:border-accent-500 transition-colors duration-300">
                <i className="ri-arrow-right-up-line text-[10px]"></i>
              </span>
            </a>
          )}
        </div>
      </div>
      {/* Elegant divider */}
      <div className="w-full h-[1px] bg-foreground-200 mt-6 md:mt-8" />
    </div>
  );
}

export default function PastEngagements() {
  const ref = useScrollReveal<HTMLDivElement>(0.08, 'fade-up');

  return (
    <section className="relative bg-background-50">
      <div
        ref={ref}
        className="px-6 md:px-10 lg:px-16 py-20 md:py-28"
      >
        <div className="max-w-6xl mx-auto">
          {/* Section label with elegant centered treatment */}
          <div className="flex items-center justify-center gap-4 mb-10 md:mb-14">
            <span className="block w-12 md:w-16 h-[1px] bg-accent-500/30" />
            <span className="text-accent-600/70 text-xs tracking-[0.2em] uppercase font-medium">
              Past Engagements
            </span>
            <span className="block w-12 md:w-16 h-[1px] bg-accent-500/30" />
          </div>

          <div>
            {pastEngagements.map((eng, index) => (
              <PastCard key={eng.id} engagement={eng} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}